import { isArtifactCanvasFirstPaintProjection } from 'flare-chat-core/contracts/canvas/artifactCanvasAdmissibility';
import { normalizeArtifactRef } from 'flare-chat-core/contracts/context/artifact-ref';

function resolveObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeTabKey(value) {
  return String(value || '').trim();
}

function normalizeText(value) {
  return String(value || '').trim();
}

function firstText(values = []) {
  const source = Array.isArray(values) ? values : [];
  return normalizeText(source.find(Boolean));
}

function hasExplicitCanvasPanelVisibility(signal = {}) {
  return Object.prototype.hasOwnProperty.call(signal, 'open_canvas_panel')
    && typeof signal.open_canvas_panel === 'boolean';
}

function resolveCanvasPanelVisibility(signal = {}) {
  if (!hasExplicitCanvasPanelVisibility(signal)) {
    return '';
  }
  return signal.open_canvas_panel ? 'open' : 'closed';
}

function isCanvasStateItem(item) {
  return String(item?.type || '').trim() === 'canvas_state';
}

function isWorkflowProjectionItem(item) {
  return String(item?.type || '').trim() === 'workflow_projection';
}

function resolveCanvasPayload(item) {
  return resolveObject(item?.payload);
}

function isAnalysisFirstPaintItem(item) {
  if (!isCanvasStateItem(item)) {
    return false;
  }
  const payload = resolveCanvasPayload(item);
  const signal = resolveObject(payload.ui_signal);
  return normalizeTabKey(signal.active_tab) === 'analysis'
    && isArtifactCanvasFirstPaintProjection(payload);
}

function resolveCanvasSignalVisibility(item, signal) {
  if (isAnalysisFirstPaintItem(item)) {
    return { open: false, visibility: 'closed' };
  }
  return {
    open: signal.open_canvas_panel === true,
    visibility: resolveCanvasPanelVisibility(signal),
  };
}

function resolveSignalKey(source = '', reason = '', activeTab = '') {
  const parts = [source, reason, activeTab].map(normalizeText);
  return parts.some(Boolean) ? parts.join(':') : '';
}

function combineSignalSource(primary = '', secondary = '') {
  const parts = [primary, secondary].map(normalizeText).filter(Boolean);
  return parts.length === 2 ? parts.join(':') : firstText(parts);
}

function normalizeCanvasSignal(item = {}) {
  const payload = resolveCanvasPayload(item);
  const signal = resolveObject(payload.ui_signal);
  const activeTab = normalizeTabKey(signal.active_tab);
  const reason = normalizeText(signal.reason);
  const signalId = normalizeText(item?.id);
  const signalRoundKey = firstText([item?.roundKey, item?.round_key]);
  const signalSource = combineSignalSource(signalId, signalRoundKey);
  const signalKey = resolveSignalKey(signalSource, reason, activeTab);
  const panelVisibility = resolveCanvasSignalVisibility(item, signal);
  return {
    signal_id: signalId,
    signal_key: signalKey,
    open_canvas_panel: panelVisibility.open,
    canvas_panel_visibility: panelVisibility.visibility,
    active_tab: activeTab,
    reason,
    mode_key: normalizeText(payload.mode_key),
  };
}

function resolveRunEventIdentity(value = {}) {
  const event = resolveObject(value);
  const payload = resolveObject(event.payload);
  return firstText([
    event.event_id,
    event.id,
    event.run_event_sequence,
    event.sequence,
    payload.event_id,
    payload.run_event_sequence,
    payload.sequence,
  ]);
}

function latestRunEventIdentity(runEvents = []) {
  const events = Array.isArray(runEvents) ? runEvents : [];
  return [...events].reverse().map(resolveRunEventIdentity).find(Boolean) ?? '';
}

function workflowProjectionSignalSource(payload = {}, item = {}) {
  const runId = firstText([payload.run_id, payload.runId]);
  const eventIdentity = latestRunEventIdentity(payload.run_events);
  const runSource = combineSignalSource(runId, eventIdentity);
  if (runSource) {
    return runSource;
  }
  return firstText([item?.roundKey, item?.round_key, item?.id]);
}

function normalizeWorkflowProjectionSignal(item = {}) {
  const payload = resolveObject(item?.payload);
  const activeTab = firstText([payload.active_tab, payload.active_canvas]);
  const reason = firstText([payload.reason, payload.next_action, payload.turn_disposition]);
  const signalId = normalizeText(item?.id);
  const signalKey = resolveSignalKey(workflowProjectionSignalSource(payload, item), reason, activeTab);
  return {
    signal_id: signalId,
    signal_key: signalKey,
    open_canvas_panel: Boolean(activeTab),
    canvas_panel_visibility: activeTab ? 'open' : '',
    active_tab: activeTab,
    reason,
    mode_key: firstText([payload.mode, payload.mode_key]),
  };
}

function normalizeSignalItem(item = {}) {
  return isWorkflowProjectionItem(item)
    ? normalizeWorkflowProjectionSignal(item)
    : normalizeCanvasSignal(item);
}

function hasCanvasSignal(item = {}) {
  if (!isCanvasStateItem(item)) {
    return false;
  }
  const signal = resolveObject(resolveCanvasPayload(item).ui_signal);
  return hasExplicitCanvasPanelVisibility(signal) || normalizeTabKey(signal.active_tab);
}

function hasWorkflowProjectionSignal(item = {}) {
  const payload = resolveObject(item.payload);
  return Boolean(isWorkflowProjectionItem(item) && firstText([payload.active_tab, payload.active_canvas]));
}

function hasAnyCanvasSignal(item = {}) {
  return hasCanvasSignal(item) || hasWorkflowProjectionSignal(item);
}

const normalizeArtifactSignal = (projection) => {
  const signal = resolveObject(projection.ui_signal);
  let ref;
  try { ref = normalizeArtifactRef(signal.artifact_ref); } catch { return normalizeCanvasSignal(); }
  const identity = (reference) => [reference?.scope?.tenant_id, reference?.scope?.project_id,
    reference?.artifact_id, reference?.artifact_version_id, reference?.artifact_type, reference?.content_digest];
  const referenceKey = JSON.stringify(identity(ref));
  const tabs = Array.isArray(projection.tabs) ? projection.tabs : [];
  const bound = tabs.filter((tab) => tab.key === projection.active_tab && tab.artifact_id === ref.artifact_id
    && JSON.stringify(identity(tab.metadata?.artifact_ref)) === referenceKey);
  if (!hasExplicitCanvasPanelVisibility(signal) || signal.active_tab !== projection.active_tab
    || projection.active_artifact_id !== ref.artifact_id || bound.length !== 1) return normalizeCanvasSignal();
  return { signal_id: referenceKey, signal_key: resolveSignalKey(referenceKey, signal.reason, signal.active_tab),
    open_canvas_panel: signal.open_canvas_panel, canvas_panel_visibility: resolveCanvasPanelVisibility(signal),
    active_tab: signal.active_tab, reason: normalizeText(signal.reason), mode_key: '' };
};

export function selectLatestCanvasSignal(timelineItems = [], { artifactTabProjection } = {}) {
  if (artifactTabProjection !== undefined) return normalizeArtifactSignal(resolveObject(artifactTabProjection));
  const item = [...(Array.isArray(timelineItems) ? timelineItems : [])]
    .reverse()
    .find(hasAnyCanvasSignal) ?? {};
  return normalizeSignalItem(item);
}

export function selectCanvasStatePresence(timelineItems = []) {
  const items = Array.isArray(timelineItems) ? timelineItems : [];
  const canvasItems = items.filter((item) => isCanvasStateItem(item));
  const latestAnalysisFirstPaintIndex = items.findLastIndex(isAnalysisFirstPaintItem);
  const currentAnalysisItems = latestAnalysisFirstPaintIndex >= 0
    ? items.slice(latestAnalysisFirstPaintIndex + 1)
    : items;
  return {
    has_analysis_canvas_state: currentAnalysisItems.some((item) => {
      if (!isCanvasStateItem(item)) {
        return false;
      }
      const payload = resolveCanvasPayload(item);
      const signal = resolveObject(payload.ui_signal);
      return normalizeTabKey(signal.active_tab) === 'analysis';
    }),
    has_sourcing_canvas_state: canvasItems.some((item) => {
      const payload = resolveCanvasPayload(item);
      const signal = resolveObject(payload.ui_signal);
      return normalizeTabKey(signal.active_tab) === 'sourcing';
    }),
  };
}

export default selectLatestCanvasSignal;
